import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Shield } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminLoginPage() {
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    name: "",
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const endpoint = isLogin ? "/admin/login" : "/admin/register";
      const payload = isLogin
        ? { email: formData.email, password: formData.password }
        : formData;

      const response = await axios.post(`${API}${endpoint}`, payload);
      
      localStorage.setItem("admin_token", response.data.token);
      localStorage.setItem("admin_user", JSON.stringify(response.data.user));
      
      toast.success(isLogin ? "Welcome back, Admin!" : "Admin account created!");
      navigate("/admin/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Authentication failed");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-4 py-16" data-testid="admin-login-page">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-block">
            <img 
              src="https://customer-assets.emergentagent.com/job_1eeea6a6-945d-4052-ae21-011aad17a3a1/artifacts/n0vcoewk_fulllogo.png" 
              alt="Astute Builders" 
              className="h-24 mx-auto mb-4"
            />
          </Link>
          <h1 className="font-['Oswald'] text-3xl font-bold text-white flex items-center justify-center gap-2">
            <Shield size={28} className="text-[#7ED4C6]" />
            Admin Portal
          </h1>
          <p className="text-[#A1A1AA] mt-2">
            {isLogin ? "Sign in to manage your business" : "Create admin account"}
          </p>
        </div>

        {/* Form */}
        <div className="bg-[#121212] border border-[#2A2A2A] p-8">
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="admin-login-form">
            {!isLogin && (
              <div>
                <label className="block text-white text-sm mb-2">Full Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required={!isLogin}
                  className="form-input w-full"
                  placeholder="Admin Name"
                  data-testid="admin-name-input"
                />
              </div>
            )}
            
            <div>
              <label className="block text-white text-sm mb-2">Email *</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="form-input w-full"
                placeholder="admin@astute-builders.com"
                data-testid="admin-email-input"
              />
            </div>
            
            <div>
              <label className="block text-white text-sm mb-2">Password *</label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                required
                minLength={6}
                className="form-input w-full"
                placeholder="••••••••"
                data-testid="admin-password-input"
              />
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="btn-primary w-full flex items-center justify-center gap-2"
              data-testid="admin-submit-btn"
            >
              {isLoading ? (
                <Loader2 size={18} className="animate-spin" />
              ) : (
                <>
                  <Shield size={18} />
                  {isLogin ? "Sign In" : "Create Admin"}
                </>
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <button
              onClick={() => setIsLogin(!isLogin)}
              className="text-[#7ED4C6] hover:text-[#9BE0D5] text-sm"
              data-testid="admin-toggle-auth"
            >
              {isLogin ? "Need to create first admin? Register" : "Already have an account? Sign in"}
            </button>
          </div>
        </div>

        {/* Client Portal Link */}
        <div className="mt-6 text-center">
          <Link to="/portal" className="text-[#A1A1AA] hover:text-white text-sm">
            ← Client Portal Login
          </Link>
        </div>
      </div>
    </div>
  );
}
